import {
    Badge,
    Box,
    Button,
    FormLabel,
    Grid,
    HStack,
    Input,
    Select,
    Stack,
    Text
} from "@chakra-ui/react";
import {useMutation, useQuery} from "@apollo/client";
import React, {KeyboardEvent, useEffect, useMemo, useState} from "react";
import {AreaLanguageOption, TextTranslation} from "../types";
import RichTextEditor from "../form/RichTextEditor";
import {getTextTranslationsQuery, setTextTranslationMutation} from "./textTranslationQueries";

export type TranslationField = {
    translationKey: string
    label: string
    fallbackValue?: string
    inputType?: "text" | "richText"
    placeholder?: string
}

type TranslationSource = "current" | "default" | "fallback" | "missing";

type Props = {
    areaId?: string
    clientId?: string
    defaultLanguage?: string
    availableLanguages?: AreaLanguageOption[]
    publishedAt?: number
    fields: TranslationField[]
    canEdit: boolean
    title?: string
    description?: string
    onTranslationSaved?: (translation: TextTranslation) => void
    showHeader?: boolean
}

const normalizeLanguageCode = (value?: string) => (value || "").trim().split("-")[0].toUpperCase();

const normalizeRichText = (value?: string) => {
    const html = (value || "").trim();
    if (!html) {
        return "";
    }

    if (typeof document === "undefined") {
        return html;
    }

    const container = document.createElement("div");
    container.innerHTML = html;
    const textContent = (container.textContent || "").replace(/\u00a0/g, " ").trim();
    if (!textContent) {
        return "";
    }

    return container.innerHTML.trim();
};

const isMissingText = (value?: string) => !(value || "").trim();

const getSourceLabel = (source: TranslationSource) => {
    switch (source) {
        case "current":
            return "Current language";
        case "default":
            return "Default language";
        case "fallback":
            return "Fallback";
        default:
            return "Missing";
    }
};

const getSourceColorScheme = (source: TranslationSource) => {
    switch (source) {
        case "current":
            return "green";
        case "default":
            return "blue";
        case "fallback":
            return "orange";
        default:
            return "gray";
    }
};

const renderPreviewText = (field: TranslationField, value?: string) => {
    if (isMissingText(value)) {
        return <Text fontSize={"sm"} color={"gray.500"}>No resolved value</Text>;
    }

    if (field.inputType === "richText") {
        return <Box
            fontSize={"sm"}
            color={"gray.700"}
            borderWidth={"1px"}
            borderColor={"gray.100"}
            borderRadius={"md"}
            padding={3}
            backgroundColor={"gray.50"}
            dangerouslySetInnerHTML={{__html: normalizeRichText(value)}}
        />;
    }

    return <Text fontSize={"sm"} color={"gray.700"}>{value}</Text>;
};

const TextTranslationEditor = ({
    areaId,
    clientId,
    defaultLanguage,
    availableLanguages = [],
    publishedAt = 0,
    fields,
    canEdit,
    title = "Translations",
    description,
    onTranslationSaved,
    showHeader = true
}: Props) => {
    const languageOptions = useMemo(() => {
        const uniqueByCode = new Map<string, AreaLanguageOption>();
        availableLanguages.forEach((language) => {
            const code = normalizeLanguageCode(language.code);
            if (!code) {
                return;
            }
            uniqueByCode.set(code, {
                ...language,
                code: code
            });
        });
        return Array.from(uniqueByCode.values()).sort((a, b) => a.code.localeCompare(b.code));
    }, [availableLanguages]);

    const normalizedDefaultLanguage = normalizeLanguageCode(defaultLanguage) || languageOptions[0]?.code || "EN";
    const [selectedLanguage, setSelectedLanguage] = useState<string>(normalizedDefaultLanguage);
    const [draftValues, setDraftValues] = useState<Record<string, string>>({});
    const [translations, setTranslations] = useState<TextTranslation[]>([]);
    const [savingKey, setSavingKey] = useState<string | undefined>(undefined);
    const languageCodes = languageOptions.map((language) => language.code);
    const translationKeys = fields.map((field) => field.translationKey);

    useEffect(() => {
        if (!languageOptions.some((language) => language.code === selectedLanguage)) {
            setSelectedLanguage(normalizedDefaultLanguage);
        }
    }, [languageOptions, normalizedDefaultLanguage, selectedLanguage]);

    const {data, loading} = useQuery(getTextTranslationsQuery, {
        variables: {
            areaId,
            clientIds: clientId ? [clientId] : [],
            languageCodes: languageCodes,
            translationKeys: translationKeys,
            publishedAt: publishedAt
        },
        skip: !areaId || !clientId || fields.length === 0 || languageCodes.length === 0,
        fetchPolicy: "network-only"
    });

    const [setTextTranslation] = useMutation(setTextTranslationMutation);

    useEffect(() => {
        const rows = data?.getTextTranslations || [];
        setTranslations(rows);
    }, [data]);

    const translationsByLanguage = useMemo(() => {
        const nextMap = new Map<string, Map<string, string>>();
        translations.forEach((translation) => {
            const languageCode = normalizeLanguageCode(translation.languageCode);
            const byKey = nextMap.get(languageCode) || new Map<string, string>();
            byKey.set(translation.translationKey, translation.text || "");
            nextMap.set(languageCode, byKey);
        });
        return nextMap;
    }, [translations]);

    useEffect(() => {
        const nextDraftValues: Record<string, string> = {};
        fields.forEach((field) => {
            nextDraftValues[field.translationKey] = translationsByLanguage.get(selectedLanguage)?.get(field.translationKey) || "";
        });
        setDraftValues(nextDraftValues);
    }, [fields, selectedLanguage, translationsByLanguage]);

    const getResolvedFieldState = (field: TranslationField) => {
        const currentValue = translationsByLanguage.get(selectedLanguage)?.get(field.translationKey);
        if (!isMissingText(currentValue)) {
            return {
                resolvedValue: currentValue,
                source: "current" as TranslationSource
            };
        }

        if (selectedLanguage !== normalizedDefaultLanguage) {
            const defaultValue = translationsByLanguage.get(normalizedDefaultLanguage)?.get(field.translationKey);
            if (!isMissingText(defaultValue)) {
                return {
                    resolvedValue: defaultValue,
                    source: "default" as TranslationSource
                };
            }
        }

        if (!isMissingText(field.fallbackValue)) {
            return {
                resolvedValue: field.fallbackValue,
                source: "fallback" as TranslationSource
            };
        }

        return {
            resolvedValue: "",
            source: "missing" as TranslationSource
        };
    };

    const getFallbackCopyValue = (field: TranslationField) => {
        const defaultValue = translationsByLanguage.get(normalizedDefaultLanguage)?.get(field.translationKey);
        if (!isMissingText(defaultValue)) {
            return defaultValue || "";
        }

        if (!isMissingText(field.fallbackValue)) {
            return field.fallbackValue || "";
        }

        return "";
    };

    const translatedCount = useMemo(() => {
        return fields.filter((field) => !isMissingText(translationsByLanguage.get(selectedLanguage)?.get(field.translationKey))).length;
    }, [fields, selectedLanguage, translationsByLanguage]);

    const mergeSavedTranslation = (savedTranslation: TextTranslation) => {
        setTranslations((previous) => {
            const filtered = previous.filter((entry) => entry.id !== savedTranslation.id &&
                !(entry.clientId === savedTranslation.clientId &&
                    normalizeLanguageCode(entry.languageCode) === normalizeLanguageCode(savedTranslation.languageCode) &&
                    entry.translationKey === savedTranslation.translationKey));
            return [...filtered, savedTranslation];
        });
    };

    const saveField = async (field: TranslationField, nextValue: string) => {
        if (!canEdit || !areaId || !clientId) {
            return;
        }
        const normalizedNextValue = field.inputType === "richText" ? normalizeRichText(nextValue) : nextValue;
        const currentStoredValue = translationsByLanguage.get(selectedLanguage)?.get(field.translationKey) || "";
        if (normalizedNextValue === currentStoredValue) {
            return;
        }

        setSavingKey(field.translationKey);
        try {
            const result = await setTextTranslation({
                variables: {
                    areaId: areaId,
                    clientId: clientId,
                    languageCode: selectedLanguage,
                    translationKey: field.translationKey,
                    text: normalizedNextValue
                }
            });
            const savedTranslation = result.data?.setTextTranslation as TextTranslation | undefined;
            if (savedTranslation) {
                mergeSavedTranslation(savedTranslation);
                onTranslationSaved?.(savedTranslation);
            }
        } finally {
            setSavingKey(undefined);
        }
    };

    const handleCopyFromFallback = async (field: TranslationField) => {
        const fallbackCopyValue = getFallbackCopyValue(field);
        if (selectedLanguage === normalizedDefaultLanguage || isMissingText(fallbackCopyValue)) {
            return;
        }
        setDraftValues((previous) => ({
            ...previous,
            [field.translationKey]: fallbackCopyValue
        }));
        await saveField(field, fallbackCopyValue);
    };

    const handleClearTranslation = async (field: TranslationField) => {
        setDraftValues((previous) => ({
            ...previous,
            [field.translationKey]: ""
        }));
        await saveField(field, "");
    };

    return (
        <Stack spacing={6}>
            {showHeader ? (
                <Box>
                    <Text fontWeight={"semibold"} marginBottom={1}>{title}</Text>
                    {description ? (
                        <Text fontSize={"sm"} color={"gray.600"}>{description}</Text>
                    ) : null}
                </Box>
            ) : description ? (
                <Text fontSize={"sm"} color={"gray.600"}>{description}</Text>
            ) : null}

            {languageOptions.length === 0 ? (
                <Text fontSize={"sm"} color={"gray.600"}>
                    Add space languages before managing translations.
                </Text>
            ) : (
                <>
                    <Grid templateColumns={{base: "1fr", md: "260px 1fr"}} gap={4} alignItems={"end"}>
                        <Box>
                            <FormLabel marginBottom={1}>Language</FormLabel>
                            <Select
                                value={selectedLanguage}
                                onChange={(event) => setSelectedLanguage(event.target.value)}
                                size={"sm"}
                            >
                                {languageOptions.map((language) => (
                                    <option key={language.code} value={language.code}>
                                        {language.label} ({language.code})
                                    </option>
                                ))}
                            </Select>
                        </Box>
                        <Text fontSize={"sm"} color={"gray.600"}>
                            Translated {translatedCount}/{fields.length} fields for {selectedLanguage}.
                            {loading ? " Loading translations..." : ""}
                        </Text>
                    </Grid>

                    <Stack spacing={5}>
                        {fields.map((field) => {
                            const resolvedState = getResolvedFieldState(field);
                            const rawValue = draftValues[field.translationKey] || "";
                            const fallbackCopyValue = getFallbackCopyValue(field);
                            return (
                                <Box key={field.translationKey} borderWidth={"1px"} borderColor={"gray.200"} borderRadius={"lg"} padding={4}>
                                    <Grid templateColumns={{base: "1fr", md: "1fr max-content"}} gap={3} marginBottom={3} alignItems={"center"}>
                                        <Box>
                                            <Text fontWeight={"medium"}>{field.label}</Text>
                                            <Text fontSize={"xs"} color={"gray.500"}>{field.translationKey}</Text>
                                        </Box>
                                        <HStack spacing={2}>
                                            <Badge colorScheme={getSourceColorScheme(resolvedState.source)}>
                                                {getSourceLabel(resolvedState.source)}
                                            </Badge>
                                            {savingKey === field.translationKey ? (
                                                <Badge colorScheme={"blue"}>Saving</Badge>
                                            ) : null}
                                        </HStack>
                                    </Grid>

                                    <Stack spacing={3}>
                                        {field.inputType === "richText" ? (
                                            <RichTextEditor
                                                value={rawValue}
                                                onChange={(value) => setDraftValues((previous) => ({
                                                    ...previous,
                                                    [field.translationKey]: value
                                                }))}
                                                onBlur={(value) => saveField(field, value)}
                                                placeholder={field.placeholder}
                                                isDisabled={!canEdit}
                                                minHeight={"120px"}
                                            />
                                        ) : (
                                            <Input
                                                size={"sm"}
                                                value={rawValue}
                                                placeholder={field.placeholder}
                                                onChange={(event) => setDraftValues((previous) => ({
                                                    ...previous,
                                                    [field.translationKey]: event.target.value
                                                }))}
                                                onBlur={(event) => saveField(field, event.target.value)}
                                                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                                                    if (event.key === "Enter") {
                                                        event.currentTarget.blur();
                                                    }
                                                }}
                                                disabled={!canEdit}
                                            />
                                        )}

                                        <HStack spacing={2} justifyContent={"space-between"} alignItems={"flex-start"}>
                                            <Box flex={1}>
                                                <Text fontSize={"xs"} color={"gray.600"} marginBottom={1}>Resolved preview</Text>
                                                {renderPreviewText(field, resolvedState.resolvedValue)}
                                            </Box>
                                            <Stack spacing={2} minWidth={"160px"}>
                                                <Button
                                                    size={"xs"}
                                                    variant={"outline"}
                                                    onClick={() => handleCopyFromFallback(field)}
                                                    isDisabled={!canEdit || selectedLanguage === normalizedDefaultLanguage || isMissingText(fallbackCopyValue)}
                                                >
                                                    Copy from fallback
                                                </Button>
                                                <Button
                                                    size={"xs"}
                                                    variant={"ghost"}
                                                    onClick={() => handleClearTranslation(field)}
                                                    isDisabled={!canEdit || isMissingText(translationsByLanguage.get(selectedLanguage)?.get(field.translationKey))}
                                                >
                                                    Clear translation
                                                </Button>
                                            </Stack>
                                        </HStack>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </>
            )}
        </Stack>
    );
};

export default TextTranslationEditor;
